import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";
import PropTypes from "prop-types";
import { useTiltifyCampaigns } from "@/hooks/useTiltifyCampaigns.js";
import useTiltifyConfig from "@/hooks/useTiltifyConfig.js";

TiltifyCampaignSelector.propTypes = {
  onCampaignSelected: PropTypes.func,
  disabled: PropTypes.bool,
  allowRetired: PropTypes.bool,
  onCampaignRetired: PropTypes.func,
  onCampaignRemoved: PropTypes.func,
  onCampaignInvalid: PropTypes.func,
  showCampaignRetired: PropTypes.bool,
  showCampaignInvalid: PropTypes.bool,
  allowTeamCampaignSelection: PropTypes.bool,
};

export default function TiltifyCampaignSelector({
  onCampaignSelected,
  disabled,
  allowRetired = false,
  onCampaignRetired = (_) => {},
  onCampaignRemoved = (_) => {},
  onCampaignInvalid = (_) => {},
  showCampaignRetired = false,
  showCampaignInvalid = false,
  allowTeamCampaignSelection = false,
}) {
  const [campaignRetired, setCampaignRetired] = useState(showCampaignRetired);
  const [campaignInvalid, setCampaignInvalid] = useState(showCampaignInvalid);
  const config = useTiltifyConfig();
  const { campaigns, campaignsLoading } = useTiltifyCampaigns({
    ...config,
    tiltifyAllowRetired: allowRetired,
  });

  const [useTeamCampaigns, setUseTeamCampaigns] = useState(
    `${config.tiltifyIsTeamCampaign}` === "true" && allowTeamCampaignSelection,
  );
  const useTeamCampaignsRef = useRef(
    `${config.tiltifyIsTeamCampaign}` === "true" && allowTeamCampaignSelection,
  );

  const currentCampaigns = useMemo(() => {
    return campaigns.filter((c) => {
      if (c.status === "retired") {
        return allowRetired;
      }

      if (Object.hasOwn(c, "team")) {
        return useTeamCampaigns;
      } else {
        return useTeamCampaigns === false;
      }
    });
  }, [campaigns, useTeamCampaigns, allowRetired]);

  useEffect(() => {
    const eventListener = (event) => {
      if (Object.hasOwn(event.data, "playliveDebugCommand")) {
        if (event.data.playliveDebugCommand === "showCampaignRetired") {
          setCampaignRetired(true);
        }
      }
    };

    window.addEventListener("message", eventListener);

    return () => {
      window.removeEventListener("message", eventListener);
    };
  }, []);

  useEffect(() => {
    setCampaignRetired(showCampaignRetired);
  }, [showCampaignRetired]);

  useEffect(() => {
    setCampaignInvalid(showCampaignInvalid);
  }, [showCampaignInvalid]);

  const updateUseTeamCampaigns = useCallback(
    (use) => {
      config.tiltifyCampaignID = "";
      config.tiltifyCampaignSlug = "";
      config.tiltifyCampaignGoal = "";
      config.tiltifyCampaignName = "";
      config.tiltifyIsTeamCampaign = use;
      localStorage.removeItem("tiltifyPollID");
      localStorage.removeItem("tiltifyCampaignID");
      localStorage.removeItem("tiltifyCampaignSlug");
      localStorage.removeItem("tiltifyCampaignGoal");
      localStorage.removeItem("tiltifyCampaignName");
      localStorage.removeItem("tiltifyIsTeamCampaign");
      setUseTeamCampaigns(use);
    },
    [config],
  );

  useEffect(() => {
    if (!allowTeamCampaignSelection && useTeamCampaigns) {
      setUseTeamCampaigns(false);
      useTeamCampaignsRef.current = false;
    }
  }, [allowTeamCampaignSelection, useTeamCampaigns]);

  const setSelectedCampaign = useCallback(
    (e) => {
      const id = e.target.value;
      if (id !== null) {
        const campaign = campaigns.find((campaign) => campaign.id === id);
        if (campaign) {
          const isTeamCampaign = Object.hasOwn(campaign, "team");

          config.tiltifyCampaignID = id;
          config.tiltifyCampaignSlug = campaign.slug;
          config.tiltifyCampaignGoal = campaign.goal.value;
          config.tiltifyCampaignName = campaign.name;
          config.tiltifyIsTeamCampaign = isTeamCampaign;

          if (isTeamCampaign) {
            config.tiltifyTeamSlug = campaign.team.slug;
            localStorage.setItem("tiltifyTeamSlug", campaign.team.slug);
          } else {
            config.tiltifyTeamSlug = "";
            localStorage.removeItem("tiltifyTeamSlug");
          }

          config.tiltifyPollID = "";

          localStorage.removeItem("tiltifyPollID");
          localStorage.setItem("tiltifyCampaignID", id);
          localStorage.setItem("tiltifyCampaignSlug", campaign.slug);
          localStorage.setItem("tiltifyCampaignGoal", campaign.goal.value);
          localStorage.setItem("tiltifyCampaignName", campaign.name);
          localStorage.setItem("tiltifyIsTeamCampaign", isTeamCampaign ? "true" : "false");

          onCampaignSelected({
            slug: config.tiltifyCampaignSlug,
            id: config.tiltifyCampaignID,
            goal: config.tiltifyCampaignGoal,
            name: config.tiltifyCampaignName,
            isTeamCampaign: config.tiltifyIsTeamCampaign,
          });
        }
      }
    },
    [campaigns, onCampaignSelected, config],
  );

  useEffect(() => {
    if (
      !campaigns ||
      campaignsLoading ||
      !config.tiltifyCampaignID ||
      config.tiltifyCampaignID.length === 0
    ) {
      return;
    }

    const currentCampaign = campaigns.find((c) => c.id === config.tiltifyCampaignID);

    if (!currentCampaign) {
      if (useTeamCampaignsRef.current !== useTeamCampaigns) {
        onCampaignRemoved(config.tiltifyCampaignID);
      }
    } else {
      if (currentCampaign.status === "retired" && !allowRetired) {
        onCampaignRetired(currentCampaign.id);
      } else if ("team" in currentCampaign && !allowTeamCampaignSelection) {
        onCampaignInvalid(currentCampaign.id);
      }
    }
  }, [
    campaigns,
    campaignsLoading,
    config.tiltifyCampaignID,
    allowRetired,
    allowTeamCampaignSelection,
    onCampaignInvalid,
    onCampaignRemoved,
    onCampaignRetired,
    useTeamCampaigns,
  ]);

  const tiltifyURL = useMemo(() => {
    if (useTeamCampaigns) {
      return "https://app.tiltify.com/teams";
    }

    return "https://app.tiltify.com/fundraiser";
  }, [useTeamCampaigns]);

  const campaignOptions = useMemo(() => {
    if (!currentCampaigns) {
      return [];
    }
    return currentCampaigns.map((campaign) => {
      let campaignName = campaign.name;

      if (campaign.status === "retired") {
        campaignName = `${campaignName} (retired)`;
      }

      if (Object.hasOwn(campaign, "team")) {
        campaignName = `${campaign.team.name}: ${campaignName}`;
      }

      return (
        <option
          title={campaign.name}
          value={campaign.id}
          key={campaign.id}
          id={campaign.id}
          selected={campaign.id === config.tiltifyCampaignID}
        >
          {campaignName}
        </option>
      );
    });
  }, [currentCampaigns, config.tiltifyCampaignID]);

  if (campaignsLoading || campaigns === undefined) {
    return (
      <div className="min-h-[135px] flex flex-col">
        {allowTeamCampaignSelection ? (
          <div className="join my-2 w-full lg:w-auto">
            <input
              className={`join-item btn ${useTeamCampaigns ? "" : "btn-secondary"}`}
              type="radio"
              name="options"
              aria-label="Personal"
              onChange={() => updateUseTeamCampaigns(false)}
              defaultChecked={!useTeamCampaigns}
            />

            <input
              className={`join-item btn ${!useTeamCampaigns ? "" : "btn-accent"}`}
              type="radio"
              name="options"
              aria-label="Team"
              onChange={() => updateUseTeamCampaigns(true)}
              defaultChecked={useTeamCampaigns}
            />
          </div>
        ) : null}

        <fieldset className="fieldset w-full">
          <legend className="fieldset-legend -mb-0.5 pb-0">
            {useTeamCampaigns ? "Team Fundraising Campaign" : "Fundraising Campaign"}
          </legend>
          <div className="select skeleton w-full" />
          <p className="fieldset-label">
            If you have no St. Jude campaigns listed here, make sure that you have them published in
            <a className="link -ml-0.5" href={tiltifyURL} rel="noopener noreferrer" target="_blank">
              Tiltify
            </a>
          </p>
        </fieldset>
      </div>
    );
  }

  return (
    <div className="min-h-[135px] flex flex-col">
      {allowTeamCampaignSelection ? (
        <div className="join my-2 w-full lg:w-auto">
          <input
            className={`join-item btn ${useTeamCampaigns ? "" : "btn-secondary"}`}
            type="radio"
            name="options"
            aria-label="Personal"
            onChange={() => updateUseTeamCampaigns(false)}
            defaultChecked={!useTeamCampaigns}
          />

          <input
            className={`join-item btn ${!useTeamCampaigns ? "" : "btn-accent"}`}
            type="radio"
            name="options"
            aria-label="Team"
            onChange={() => updateUseTeamCampaigns(true)}
            defaultChecked={useTeamCampaigns}
          />
        </div>
      ) : null}

      <fieldset className="fieldset">
        <legend className="fieldset-legend -mb-0.5 pb-0">
          {useTeamCampaigns ? "Team Fundraising Campaign" : "Fundraising Campaign"}
        </legend>

        <select
          disabled={disabled}
          className="select select-bordered w-full"
          onChange={setSelectedCampaign}
          value={config.tiltifyCampaignID?.length ? config.tiltifyCampaignID : null}
        >
          <option
            title="Please select a campaign"
            value="null"
            disabled
            selected={!config.tiltifyCampaignID || config.tiltifyCampaignID.length === 0}
          >
            Please select a campaign
          </option>
          {campaignOptions}
        </select>

        {!campaignRetired && (
          <p className="fieldset-label">
            If you have no St. Jude campaigns listed here, make sure that you have them published in
            <a className="link -ml-0.5" href={tiltifyURL} rel="noopener noreferrer" target="_blank">
              Tiltify
            </a>
          </p>
        )}

        {campaignRetired && (
          <p className="fieldset-label text-error">Your previous campaign has been retired</p>
        )}

        {campaignInvalid && (
          <p className="fieldset-label text-error">
            Your previously selected campaign cannot be used
          </p>
        )}
      </fieldset>
    </div>
  );
}
